import { Store } from 'redux';
import fs from 'app/native/node/fs';
import path from 'app/native/node/path';
import ApiCalls from 'app/utils/apiCalls';
import logger from 'app/utils/debug/logger';
import isAcquisitionSoftware from 'app/utils/isAcquisitionSoftware';
import {
  AutomaticCleanupMode,
  setLastWarningThreshold,
  StoreWithStorage,
} from 'app/redux/storage/reducer';
import _ from 'lodash';
import { Study } from 'app/interfaces/Study';

// Storage thresholds in bytes
const GB = 1024 * 1024 * 1024;
const STORAGE_SUFFICIENT_THRESHOLD = 30 * GB; // 30GB
const STORAGE_CRITICAL_THRESHOLD = 25 * GB; // 25GB

interface StorageUsage {
  totalSize: number;
  availableSize: number;
}

interface StorageUsagePayload {
  usage: StorageUsage;
  directory: string;
  deviceName: string;
  deviceIdentifier: string;
}

/**
 * Return the studyId and the imageId from the file path if the image match the {roootDir}/{studyId}/{imageId}.dcm pattern, else return undefined
 */
function getIdsFromPath(
  filePath: string,
  rootDir: string
): { studyId: string; imageId: string } | undefined {
  const fileBaseDir = path().relative(rootDir, filePath);
  const splittedPath = fileBaseDir.split(path().sep);
  if (splittedPath.length !== 2) return undefined; // Does not match the image pattern
  const filename = path().parse(splittedPath[1]);
  if (!filename.ext.toLowerCase().endsWith('.dcm')) {
    return undefined;
  } // Not a DICOM file

  const studyId = splittedPath[0];
  const imageId = filename.name; // Remove the extension;
  return { studyId, imageId };
}

async function isFileDeletionAllowed({
  filePath,
  rootDir,
  mode,
  studyInfoCache = {},
}: {
  filePath: string;
  rootDir: string;
  mode: AutomaticCleanupMode;
  studyInfoCache: Record<string, Study>;
}) {
  if (mode === 'none') return false; // No cleanup needed
  if (mode === 'all') return true; // Cleanup always needed

  const { studyId, imageId } = getIdsFromPath(filePath, rootDir) ?? {};

  // Only perform checks if the file is a DICOM file
  if (!studyId || !imageId) return true;

  if (studyId && imageId) {
    try {
      let studyInfo;
      if (studyInfoCache[studyId]) {
        studyInfo = studyInfoCache[studyId];
      } else {
        studyInfo = (await ApiCalls.getStudies([studyId]))[0];
        studyInfoCache[studyId] = studyInfo; // Cache the study info
      }
      // console.debug(`Study info: `, JSON.stringify(studyInfo, null, 2));
      const image = _.find(studyInfo.images, { _id: imageId });
      if (image && !image?.image_metadata?.PACS?.isSync) {
        logger.info(`Image not sync: Skipping not sync file deletion ${filePath} `);
        return false;
      }
    } catch (err) {
      // Ignore errors related to study retrieval
      logger.info(`Study retrieval error: Skipping not sync file deletion ${filePath} `);
      logger.error(`Failed to retrieve study info for ${studyId}:`, err);
      return false;
    }
  }
  return true;
}
export default class StorageUsageMonitor {
  constructor(
    public deviceName: string,
    public deviceIdentifier: string,
    public store: Store<StoreWithStorage>,
    public showStorageWarning: () => void
  ) {}

  private async deleteOldestFiles(
    directory: string,
    targetFreeSpace: number,
    mode: AutomaticCleanupMode
  ): Promise<void> {
    if (mode === 'none') return; // No cleanup needed

    const studyInfoCache: Record<string, Study> = {};

    try {
      const directoryStats = await fs().promises.statfs(directory);
      let availableSize = directoryStats.bavail * directoryStats.bsize;

      if (availableSize >= targetFreeSpace) {
        return; // Already enough space
      }

      // Get all files in the directory with their stats
      const dirEntries = await fs().promises.readdir(directory, {
        recursive: true,
        withFileTypes: true,
      });
      const fileStats = await Promise.all(
        dirEntries.map(async (entry) => {
          if (!entry.isFile()) return undefined;
          const fullPath = path().join(entry.parentPath, entry.name);
          const stats = await fs().promises.stat(fullPath);
          return { name: entry.name, path: fullPath, stats };
        })
      );

      // Sort files by creation time (oldest first)
      fileStats.sort((a, b) => a.stats.birthtimeMs - b.stats.birthtimeMs);

      // Delete oldest files until we reach the target free space
      for (const file of fileStats) {
        if (availableSize >= targetFreeSpace) {
          break;
        }
        const isDeletionAllowed = await isFileDeletionAllowed({
          filePath: file.path,
          rootDir: directory,
          mode,
          studyInfoCache,
        });
        if (!isDeletionAllowed) {
          continue;
        }

        try {
          await fs().promises.unlink(file.path);
          logger.info(`Deleted old file: ${file.path} (${file.stats.size} bytes)`);

          // Update available size
          availableSize += file.stats.size;
        } catch (err) {
          logger.error(`Failed to delete file: ${file.path}`, err);
        }
      }
    } catch (err) {
      logger.error('Error during file cleanup:', err);
    }
  }

  private async handleRollingCleanup(directory: string, availableSize: number): Promise<void> {
    const state = this.store.getState();
    const { automaticCleanupMode, lastWarningThreshold } = state.storage;

    // If available space is sufficient, no action needed
    if (availableSize >= STORAGE_SUFFICIENT_THRESHOLD) {
      return;
    }

    // Check if user has already acknowledged rolling cleanup
    if (automaticCleanupMode !== 'none') {
      // If acknowledged and space is critical, perform cleanup
      if (availableSize < STORAGE_CRITICAL_THRESHOLD) {
        await this.deleteOldestFiles(directory, STORAGE_SUFFICIENT_THRESHOLD, automaticCleanupMode);
      }
      return;
    }

    // Don't show warning if we've already warned recently at this threshold
    const shouldShowWarning =
      lastWarningThreshold === null || availableSize <= lastWarningThreshold - GB;
    if (!shouldShowWarning) {
      return;
    }

    // Show warning and update threshold
    this.showStorageWarning();
    const newThreshold = Math.floor(availableSize / GB) * GB;
    this.store.dispatch(setLastWarningThreshold(newThreshold));

    // No further action if user didn't acknowledge
  }

  updateStorageUsageForDirectory = async (directory: string): Promise<void> => {
    if (!fs() || !isAcquisitionSoftware()) return;

    try {
      const directoryStats = await fs().promises.statfs(directory);

      const availableSize = directoryStats.bavail * directoryStats.bsize;

      this.handleRollingCleanup(directory, availableSize);

      ApiCalls.updateStorageUsageForDirectory({
        usage: {
          totalSize: directoryStats.blocks * directoryStats.bsize,
          availableSize: availableSize,
        },
        directory,
        deviceName: this.deviceName,
        deviceIdentifier: this.deviceIdentifier,
      } as StorageUsagePayload).catch((error: any) => {
        // Ignore permission disabled error
        if (error.response?.status !== 401) {
          throw error;
        }
      });
    } catch (err) {
      logger.error(err);
    }
  };
}
